import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, type IconName } from '../components/Icon'
import { usePaletteChoice, useThemeChoice, type PaletteChoice, type ThemeChoice } from '../lib/theme'

const OPTIONS: { value: ThemeChoice; icon: IconName; key: 'themeSystem' | 'themeLight' | 'themeDark' }[] = [
  { value: 'system', icon: 'contrast', key: 'themeSystem' },
  { value: 'light', icon: 'light_mode', key: 'themeLight' },
  { value: 'dark', icon: 'dark_mode', key: 'themeDark' },
]

/** Auto (follow the phone), light or dark. The choice is remembered on this device. */
export function ThemeSwitcher() {
  const { t } = useTranslation()
  const name = useId()
  const [choice, setChoice] = useThemeChoice()
  return (
    <fieldset className="theme-switcher">
      <legend className="theme-switcher__legend">{t('app.footer.appearance')}</legend>
      <div className="theme-switcher__options">
        {OPTIONS.map((option) => (
          <label key={option.value} className="theme-switcher__option">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={choice === option.value}
              onChange={() => setChoice(option.value)}
            />
            <span className="theme-switcher__pill">
              <Icon name={option.icon} size={18} />
              {t(`app.footer.${option.key}`)}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

const PALETTE_OPTIONS: {
  value: PaletteChoice
  swatch: string
  key: 'paletteFern' | 'paletteSky' | 'palettePowder' | 'paletteBlueprint'
}[] = [
  { value: 'fern', swatch: '#355e3f', key: 'paletteFern' },
  { value: 'sky', swatch: '#1f5f99', key: 'paletteSky' },
  { value: 'powder', swatch: '#2f5d8a', key: 'palettePowder' },
  { value: 'blueprint', swatch: '#4b4fd6', key: 'paletteBlueprint' },
]

/** The light-mode colours: fern (default), sky, powder or blueprint. Remembered on this device. */
export function PaletteSwitcher() {
  const { t } = useTranslation()
  const name = useId()
  const [choice, setChoice] = usePaletteChoice()
  return (
    <fieldset className="theme-switcher">
      <legend className="theme-switcher__legend">{t('app.footer.colours')}</legend>
      <div className="theme-switcher__options">
        {PALETTE_OPTIONS.map((option) => (
          <label key={option.value} className="theme-switcher__option">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={choice === option.value}
              onChange={() => setChoice(option.value)}
            />
            <span className="theme-switcher__pill">
              <span className="theme-switcher__swatch" style={{ background: option.swatch }} aria-hidden="true" />
              {t(`app.footer.${option.key}`)}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
